import '@i18n/i18n';
import './styles/index.css';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import routes from 'src/configs/routesConfig';
import { initSentry, Sentry } from '@/lib/sentry';

initSentry();

async function mockSetup() {
	if (!import.meta.env.DEV) return;

	try {
		const { worker } = await import('@mock-utils/mswMockAdapter');
		await worker.start({
			onUnhandledRequest: 'bypass'
		});
	} catch (error) {
		// Never block boot on the mock layer. This used to be an unguarded
		// `.then()`, so any failure to register the service worker — a browser
		// that blocks them, a private window, an embedded webview — left the
		// app on its splash screen forever with only a console error to explain
		// it. Dev-only code should not be able to do that.
		//
		// MSW only serves the template's mock auth endpoints; the real admin
		// talks to the storefront API, so losing it degrades nothing we use.
		// console.error, not warn: the admin eslint config allows only `error`.
		console.error('[msw] mock worker failed to start; continuing without it', error);
	}
}

/**
 * The root element of the application.
 */
const container = document.getElementById('app');

if (!container) {
	throw new Error('Failed to find the root element');
}

mockSetup().then(() => {
	/**
	 * The root component of the application.
	 */
	const root = createRoot(container, {
		onUncaughtError: (error, errorInfo) => {
			console.error('UncaughtError error', error, errorInfo.componentStack);
			Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
		},
		onCaughtError: (error, errorInfo) => {
			console.error('Caught error', error, errorInfo.componentStack);
			Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
		}
	});

	const router = createBrowserRouter(routes);

	root.render(
		<Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong.</div>}>
			<RouterProvider router={router} />
		</Sentry.ErrorBoundary>
	);
});
